import { Avatar, Box, Button, Grid } from "@mui/material";
import React, { useState } from "react";
import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { useModals } from "../../context/ModalContext";
import { useVendors } from "./hooks/useVendors";
import { VendorForm } from "./VendorForm";

interface VendorCreateProps {
  onDone?: () => void;
  sx?: any;
  initialPhoneNumber?: string;
  status?: string;
}

export const VendorCreate: React.FC<VendorCreateProps> = ({ onDone = null, sx = {}, initialPhoneNumber, status = "ACTIVE" }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useGlobalSnackbar();
  const { closeByKey } = useModals();
  const { createNewVendor } = useVendors();

  const isProspect = status === "PROSPECT";

  const handleFinish = async (formData: any) => {
    setIsLoading(true);

    const { phone, phones, emails, email, ...vendorForm_ } = formData || {};

    // Format phone numbers
    const mappedPhoneNumbers = Array.isArray(phones)
      ? phones
          .map((p) => {
            const cleanPhone = typeof p.phone === "string" ? p.phone.replace(/\D/g, "") : "";
            const dialCode = p.dialCode || "+1";
            return {
              countryCode: p.countryCode || "US",
              dialCode: dialCode,
              phone: cleanPhone,
              phoneNumber: dialCode + cleanPhone,
              tag: p.tag || "primary",
              isDefault: !!p.isDefault,
            };
          })
          .filter((p) => p.phone)
      : [];

    // Format emails
    const mappedEmails = Array.isArray(emails)
      ? emails
          .map((e) => ({
            email: e.email || e.value || "",
            tag: e.tag || "primary",
            isDefault: !!e.isDefault,
          }))
          .filter((e) => e.email)
      : [];

    const payload = {
      ...vendorForm_,
      address: vendorForm_.address || null,
      city: vendorForm_.city || null,
      state: vendorForm_.state || null,
      country: vendorForm_.country || null,
      zipCode: vendorForm_.zipCode || null,
      status: status || vendorForm_.status || "ACTIVE",
      vendorCompanyName: vendorForm_.type === "CORPORATE" ? vendorForm_.vendorCompanyName || null : null,
      alias: vendorForm_.type === "CORPORATE" ? vendorForm_.alias || null : null,
      emails: mappedEmails,
      phoneNumbers: mappedPhoneNumbers,
    };

    try {
      await createNewVendor(payload);
      showSnackbar(isProspect ? "Vendor prospect added successfully" : "Vendor added successfully", "success");
      closeByKey("Dashboard-VendorAdd-Business");
      onDone?.();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || err.message || "Failed to create vendor", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", height: "100%", ...sx }}>
      <Grid container spacing={2} sx={{ mt: { xs: 1, md: 3 }, height: "100%" }}>
        <Grid
          item
          xs={12}
          md={2}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            pt: 4,
          }}
        >
          <Avatar sx={{ width: 100, height: 100, bgcolor: "orange.light" }} />
        </Grid>

        <Grid
          item
          xs={12}
          md={10}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
          }}
        >
          <VendorForm onSubmitCreate={handleFinish} initialPhoneNumber={initialPhoneNumber} status={status} isProspect={isProspect} />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, px: { xs: 1.5, sm: 3 }, mt: 2, mb: 4 }}>
            <Button
              onClick={() => closeByKey("Dashboard-VendorAdd-Business")}
              sx={{
                px: 4,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                color: "var(--fx-surface-textsecondary, #64748B)",
                border: "1px solid var(--fx-surface-borderstrong, #CBD5E1)",
                backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                "&:hover": { backgroundColor: "var(--fx-surface-muted, #F1F5F9)" },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="vendor-form-general-info"
              disabled={isLoading}
              sx={{
                px: 4,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: "#ea580c",
                "&:hover": { backgroundColor: "#ea580c" },
              }}
            >
              {isLoading ? "Saving..." : isProspect ? "Create Vendor Prospect" : "Create Vendor"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VendorCreate;
